<template>
  <div class="card card-custom gutter-b">
    <div class="card-header border-0 py-5">
      <div>
        <h3 class="card-title font-weight-bolder text-dark mb-1">Relatório por centro de custos</h3>
        <div class="text-muted">Analise as despesas agrupadas por período</div>
      </div>
      <div class="card-toolbar">
        <router-link v-if="!embedded" :to="{ name: 'centrosCustos' }" class="btn btn-light">Voltar ao cadastro</router-link>
      </div>
    </div>

    <div class="card-body pt-0">
      <b-row>
        <b-col :md="embedded ? 6 : 3">
          <b-form-group label="Data inicial">
            <b-form-input type="date" v-model="filtros.data_inicial" />
          </b-form-group>
        </b-col>
        <b-col :md="embedded ? 6 : 3">
          <b-form-group label="Data final">
            <b-form-input type="date" v-model="filtros.data_final" />
          </b-form-group>
        </b-col>
        <b-col :md="embedded ? 6 : 3">
          <b-form-group label="Regime">
            <b-form-select v-model="filtros.regime">
              <b-form-select-option value="competencia">Competência</b-form-select-option>
              <b-form-select-option value="caixa">Caixa</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col :md="embedded ? 6 : 3">
          <CentroCustoSelect
            v-model="filtros.id_centro_custo"
            label="Filtrar centro de custo"
            :allow-create="false"
          />
        </b-col>
      </b-row>
      <div class="d-flex justify-content-between align-items-center flex-wrap mb-5">
        <b-form-checkbox v-model="filtros.incluir_sem_centro">Incluir despesas sem centro de custo</b-form-checkbox>
        <div class="d-flex mt-2 mt-md-0">
          <button class="btn btn-primary mr-2" :disabled="carregando || gerandoPdf" @click="gerar">
            <b-spinner v-if="carregando" small class="mr-1" /> Visualizar
          </button>
          <button class="btn btn-light-primary" :disabled="carregando || gerandoPdf" @click="gerarPdf">
            <b-spinner v-if="gerandoPdf" small class="mr-1" /> Gerar PDF
          </button>
        </div>
      </div>

      <template v-if="relatorio">
        <b-row class="mb-5">
          <b-col md="4"><div class="resumo"><small>Total de lançamentos</small><strong>{{ relatorio.quantidade }}</strong></div></b-col>
          <b-col md="4"><div class="resumo"><small>Total geral</small><strong>{{ moeda(relatorio.total_geral) }}</strong></div></b-col>
          <b-col md="4"><div class="resumo"><small>Centros encontrados</small><strong>{{ relatorio.centros_custos.length }}</strong></div></b-col>
        </b-row>

        <div class="table-responsive">
          <table class="table table-head-custom table-vertical-center">
            <thead><tr><th>Centro de custo</th><th class="text-center">Lançamentos</th><th class="text-right">Total</th></tr></thead>
            <tbody>
              <template v-for="grupo in relatorio.centros_custos">
                <tr :key="`grupo-${grupo.id_centro_custo || 'sem'}`" class="cursor-pointer" @click="alternar(grupo)">
                  <td class="font-weight-bold">{{ grupo.nome }}</td>
                  <td class="text-center">{{ grupo.quantidade }}</td>
                  <td class="text-right font-weight-bolder">{{ moeda(grupo.total) }}</td>
                </tr>
                <tr v-if="abertos[chave(grupo)]" :key="`detalhe-${grupo.id_centro_custo || 'sem'}`">
                  <td colspan="3" class="p-0">
                    <table class="table table-sm mb-0 bg-light">
                      <tbody>
                        <tr v-for="item in grupo.lancamentos || []" :key="`${item.origem}-${item.id}`">
                          <td>{{ formatarData(item.data) }}</td>
                          <td>{{ item.descricao || item.documento || '-' }}</td>
                          <td>{{ item.fornecedor || '-' }}</td>
                          <td class="text-right">{{ moeda(item.valor) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
              <tr v-if="relatorio.centros_custos.length === 0"><td colspan="3" class="text-center text-muted">Nenhuma despesa encontrada.</td></tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import CentroCustoSelect from "@/components/CentroCustoSelect/CentroCustoSelect.vue";

const hoje = new Date();
const dataLocal = (data) => {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
};

export default {
  name: "RelatorioCentroCusto",
  components: { CentroCustoSelect },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filtros: {
        data_inicial: dataLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
        data_final: dataLocal(hoje),
        regime: "competencia",
        id_centro_custo: null,
        incluir_sem_centro: true,
        detalhado: true,
      },
      carregando: false,
      gerandoPdf: false,
      relatorio: null,
      abertos: {},
    };
  },
  methods: {
    periodoValido() {
      if (!this.filtros.data_inicial || !this.filtros.data_final) {
        this.$toast.warning("Informe as datas para gerar o relatório!");
        return false;
      }

      if (this.filtros.data_final < this.filtros.data_inicial) {
        this.$toast.warning("A data final deve ser igual ou posterior à data inicial!");
        return false;
      }

      return true;
    },
    async gerar() {
      if (!this.periodoValido()) return;

      this.carregando = true;
      try {
        const { data } = await this.$http.post("/relatorios/centros-custos", this.filtros);
        this.relatorio = data.data || data;
        this.abertos = {};
      } finally {
        this.carregando = false;
      }
    },
    async gerarPdf() {
      if (!this.periodoValido()) return;

      this.gerandoPdf = true;
      try {
        const { data } = await this.$http.post("/relatorios/centros-custos", {
          ...this.filtros,
          gerar_pdf: true,
        });

        if (!data.status) {
          this.$toast.warning(data.message || "Não há dados para gerar o relatório.");
          return;
        }

        this.$toast.success(
          data.message || "Solicitação do PDF realizada com sucesso!"
        );
      } finally {
        this.gerandoPdf = false;
      }
    },
    chave(grupo) {
      return grupo.id_centro_custo || "sem-centro";
    },
    alternar(grupo) {
      const chave = this.chave(grupo);
      this.$set(this.abertos, chave, !this.abertos[chave]);
    },
    moeda(valor) {
      return Number(valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    formatarData(data) {
      if (!data) return "-";
      return String(data).slice(0, 10).split("-").reverse().join("/");
    },
  },
};
</script>

<style scoped>
.resumo { background: #f3f6f9; border-radius: 6px; display: flex; flex-direction: column; padding: 16px; }
.resumo small { color: #7e8299; }
.resumo strong { font-size: 1.35rem; margin-top: 4px; }
</style>
